import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Database, RefreshCw } from "lucide-react";
import { useCloseSync, useMergeProgress } from "@/hooks/useEventStream";
import type { MergeProgress } from "@/api/types";
import type { MergeProgressEvent } from "@/hooks/useEventStream";

/**
 * Unified surface for in-flight background jobs — close-sync and the
 * post-finalize inverted-index merge today; easy to add new job types.
 *
 * Each active job is one compact row: icon, label, phase, progress bar,
 * throughput, ETA. The card hides itself entirely when nothing is
 * running so the page collapses cleanly. Built for /runs (index page).
 *
 * Reads two SSE feeds:
 *   - `close_sync` via {@link useCloseSync} — published on every state-doc
 *     mutation by the close-sync code path.
 *   - `merge_progress` via {@link useMergeProgress} — published by the
 *     background container every ~2 s during the merge pipeline phases.
 *
 * Terminal rows ("done"/"failed") linger for ~6 s with a checkmark/error
 * mark so the user sees confirmation, then fade out.
 */
export function BackgroundActivityCard() {
  // Tick once a second so elapsed-time labels stay live without waiting on
  // the next SSE event (matches ActiveRunsPanel pattern).
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const closeSync = useCloseSync();
  const merges = useMergeProgress();

  const rows: React.ReactNode[] = [];

  if (closeSync?.in_progress) {
    rows.push(<CloseSyncRow key="close" />);
  }

  for (const [runId, evt] of merges) {
    // Hide rows whose terminal phase ended >6 s ago. The Map keeps them
    // around so the SPA can flash a checkmark, but we don't want stale
    // entries cluttering the card forever.
    const phase = evt.progress.phase;
    if (phase === "done" || phase === "failed") {
      const age = Date.now() - new Date(evt.progress.updated_at).getTime();
      if (age > 6_000) continue;
    }
    rows.push(<MergeRow key={runId} evt={evt} />);
  }

  if (rows.length === 0) return null;

  return (
    <section className="card p-4 space-y-2">
      <h2 className="text-xs uppercase tracking-wide text-muted font-semibold">
        Background activity
      </h2>
      <div className="divide-y divide-border/60">
        {rows}
      </div>
    </section>
  );
}

// ---------- close-sync row -------------------------------------------------

function CloseSyncRow() {
  const status = useCloseSync();
  if (!status?.in_progress) return null;
  const elapsed = elapsedSince(status.running_started_at);
  const rate = elapsed > 0 ? status.running_processed / elapsed : 0;
  const variant = status.running_force_full ? "(full)" : "(incremental)";

  return (
    <ActivityRow
      icon={<RefreshCw className="h-3.5 w-3.5 text-accent animate-spin" />}
      label={`Close sync ${variant}`}
      phase={`Pulled ${status.running_processed.toLocaleString()} lead${status.running_processed === 1 ? "" : "s"}`}
      bar={null}
      meta={
        <>
          {rate > 0 ? <Stat value={`${rate.toFixed(0)}/s`} /> : null}
          <Stat value={formatDuration(elapsed)} label="elapsed" />
        </>
      }
    />
  );
}

// ---------- inverted-index merge row --------------------------------------

function MergeRow({ evt }: { evt: MergeProgressEvent }) {
  const { run_id, source, progress } = evt;
  const { phase } = progress;

  const phaseLabel = PHASE_LABELS[phase] ?? phase;
  const cleanedSource = source.replace(/^source:/, "");
  const elapsed = elapsedSince(progress.started_at);

  const { pct, primary, rateLabel, eta } = describePhase(progress, elapsed);

  return (
    <ActivityRow
      icon={
        phase === "failed" ? (
          <Database className="h-3.5 w-3.5 text-danger" />
        ) : phase === "done" ? (
          <Database className="h-3.5 w-3.5 text-success" />
        ) : (
          <Database className="h-3.5 w-3.5 text-accent animate-pulse" />
        )
      }
      label={
        <>
          <span className="text-muted">Indexing</span>{" "}
          <span className="font-mono text-xs">{cleanedSource}</span>
        </>
      }
      phase={
        phase === "failed" ? (
          <span className="text-danger">{phaseLabel}{progress.error ? ` — ${progress.error}` : ""}</span>
        ) : (
          <>
            {phaseLabel}
            {primary ? <> · {primary}</> : null}
          </>
        )
      }
      bar={
        phase === "sort"
          ? <IndeterminateBar />
          : pct != null
          ? <ProgressBar pct={pct} />
          : null
      }
      meta={
        <>
          {rateLabel ? <Stat value={rateLabel} /> : null}
          {eta != null ? <Stat value={formatDuration(eta)} label="ETA" /> : null}
          <Link
            to="/runs/$runId"
            params={{ runId: run_id }}
            className="text-accent hover:underline inline-flex items-center text-xs"
          >
            run <ArrowRight className="h-3 w-3 ml-0.5" />
          </Link>
        </>
      }
    />
  );
}

const PHASE_LABELS: Record<MergeProgress["phase"], string> = {
  emit: "Decoding forward index",
  sort: "Sorting on disk…",
  write: "Encoding postings",
  done: "Merged",
  failed: "Failed",
};

/**
 * Phase-specific bar/rate/ETA reducer. Centralised so each phase's display
 * logic is in one place rather than scattered conditionals in the row JSX.
 */
function describePhase(progress: MergeProgress, elapsed: number): {
  pct: number | null;
  primary: string | null;
  rateLabel: string | null;
  eta: number | null;
} {
  if (progress.phase === "emit") {
    const total = progress.domains_total;
    const done = progress.domains_emitted;
    const pct = total && total > 0 ? Math.min(100, (done / total) * 100) : null;
    // Throughput as pairs/sec — it's what's actually expensive per unit work.
    const rate = elapsed > 0 ? progress.pairs_emitted / elapsed : 0;
    const remaining = total != null ? Math.max(0, total - done) : null;
    const domainRate = elapsed > 0 ? done / elapsed : 0;
    const eta =
      remaining != null && domainRate > 0 ? remaining / domainRate : null;
    return {
      pct,
      primary:
        total != null
          ? `${compact(done)} / ${compact(total)} domains`
          : `${compact(done)} domains`,
      rateLabel: rate > 0 ? `${compact(Math.round(rate))} pairs/s` : null,
      eta,
    };
  }
  if (progress.phase === "write") {
    const total = progress.word_ids_total;
    const done = progress.word_ids_written;
    const pct = total && total > 0 ? Math.min(100, (done / total) * 100) : null;
    const rate = elapsed > 0 ? done / elapsed : 0;
    const remaining = total != null ? Math.max(0, total - done) : null;
    const eta = remaining != null && rate > 0 ? remaining / rate : null;
    return {
      pct,
      primary:
        total != null
          ? `${compact(done)} / ${compact(total)} word ids`
          : `${compact(done)} word ids`,
      rateLabel: rate > 0 ? `${compact(Math.round(rate))} ids/s` : null,
      eta,
    };
  }
  // sort, done, failed: no quantitative progress display.
  return { pct: null, primary: null, rateLabel: null, eta: null };
}

// ---------- shared row chrome ---------------------------------------------

function ActivityRow({
  icon,
  label,
  phase,
  bar,
  meta,
}: {
  icon: React.ReactNode;
  label: React.ReactNode;
  phase: React.ReactNode;
  bar: React.ReactNode;
  meta: React.ReactNode;
}) {
  return (
    <div className="py-2 flex items-center gap-3 text-sm">
      <div className="flex-shrink-0">{icon}</div>
      <div className="flex-shrink-0 min-w-[12rem]">{label}</div>
      <div className="text-xs text-muted flex-shrink-0 min-w-[10rem]">
        {phase}
      </div>
      <div className="flex-grow min-w-[6rem]">{bar}</div>
      <div className="flex items-center gap-3 flex-shrink-0 text-xs">
        {meta}
      </div>
    </div>
  );
}

function ProgressBar({ pct }: { pct: number }) {
  return (
    <div className="h-1.5 rounded-full bg-border/40 overflow-hidden">
      <div
        className="h-full bg-accent transition-[width] duration-500"
        style={{ width: `${pct.toFixed(1)}%` }}
      />
    </div>
  );
}

function IndeterminateBar() {
  return (
    <div className="h-1.5 rounded-full bg-border/40 overflow-hidden">
      <div className="h-full w-1/3 bg-accent/60 animate-pulse" />
    </div>
  );
}

function Stat({ value, label }: { value: string; label?: string }) {
  return (
    <span className="tabular-nums">
      {label ? <span className="text-muted mr-1">{label}</span> : null}
      {value}
    </span>
  );
}

// ---------- helpers --------------------------------------------------------

function elapsedSince(iso: string | null): number {
  if (!iso) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
}

function formatDuration(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const totalMin = Math.round(seconds / 60);
  if (totalMin < 60) return `${totalMin}m`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h < 24) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh > 0 ? `${d}d ${rh}h` : `${d}d`;
}

function compact(n: number): string {
  if (n < 1000) return n.toString();
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}K`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}
